/* Is this chat card a hit or a heal?
 *
 * Reported from the table, 2026-09-29: a Healing Word card offered "Damage 11"
 * with an "also reduce max HP" tickbox beside it. Three wrongs in one row — the
 * wrong word, a number that disagreed with the card's own "Healing 10" pill, and
 * an option that is meaningless for a beneficial spell.
 *
 * The cause was a single predicate: "is there a row with `damage` in its label".
 * A Beyond20 healing card can satisfy that, because the dice row is often labelled
 * for the dice rather than for the effect — so the card's INTENT has to be read
 * before its numbers.
 *
 * Pulled out of the component so it can be tested. The original lived inline in a
 * 1,700-line file and was wrong in three ways at once without anything noticing.
 */
export interface CardRow { label: string; total: number | null }

const HEAL = /heal|cure|restor/i;
const HURT = /damage|dmg/i;

/** The row that heals, if any. */
export const healRow = (rows: CardRow[]): CardRow | null =>
  rows.find((r) => HEAL.test(r.label) && r.total !== null) || null;

/** The row that hurts — and a row that heals is never it, however it is labelled. */
export const hurtRow = (rows: CardRow[]): CardRow | null =>
  rows.find((r) => HURT.test(r.label) && !HEAL.test(r.label) && r.total !== null) || null;

/* A card heals when it has a healing row and nothing that is plainly damage.
   Both together is a real thing — a paladin's smite beside a lay on hands — and it
   is treated as a hit, because applying the damage and asking about the healing is
   the safer way round: a missed heal is visible to the player it was meant for, and
   a missed hit is not. */
export const healsOnly = (rows: CardRow[]): boolean => Boolean(healRow(rows)) && !hurtRow(rows);

export const healOf = (rows: CardRow[]): number | null => healRow(rows)?.total ?? null;
export const damageOf = (rows: CardRow[]): number | null => (healsOnly(rows) ? null : hurtRow(rows)?.total ?? null);

/** What the quick-apply row should offer, and as what. */
export const applyAmountOf = (rows: CardRow[]): number | null => (healsOnly(rows) ? healOf(rows) : damageOf(rows));

/* DOES IT LAND?
 *
 * Pulled out of CardFeed for S6-1, when the three-click apply became one: the
 * button is disabled on a miss, so "did it miss" is now the thing standing
 * between a card and a player's hit points. That belongs in a tested function
 * rather than in a closure inside a render.
 *
 * Deliberately conservative. "Unknown" is not "miss": a target whose AC nobody
 * knows (a creature with no AC bar on its token) leaves the decision with the
 * DM rather than refusing the apply, because refusing silently is the failure
 * this project keeps fixing. A natural 20 always lands and a natural 1 never
 * does, whatever the arithmetic says.
 */
export interface AttackRoll { total: number | null; crit?: boolean; fumble?: boolean }
export type Landing = "hit" | "miss" | "unknown";

export function landing(attack: AttackRoll | null | undefined, ac: number | null | undefined): Landing {
  if (!attack) return "unknown";          // no attack roll: not an attack, nothing to miss with
  if (attack.fumble) return "miss";
  if (attack.crit) return "hit";
  if (ac === null || ac === undefined || !Number.isFinite(ac)) return "unknown";
  /* Number(null) is 0 AND finite, so a null total would read as a miss against
     any AC — the "unknown is not zero" trap, which has bitten this project at six
     other boundaries. An absent total is an absent roll. */
  if (attack.total === null || attack.total === undefined) return "unknown";
  const total = Number(attack.total);
  if (!Number.isFinite(total)) return "unknown";
  return total >= ac ? "hit" : "miss";
}

/** Should the apply be refused? Only a known miss, and only while the DM has
 *  the hit/miss automation on. */
export const blockedByMiss = (attack: AttackRoll | null | undefined, ac: number | null | undefined, hitVsAc: boolean): boolean =>
  hitVsAc && landing(attack, ac) === "miss";
